import { FC, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Mail } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfileDrawer } from "@/contexts/ProfileDrawerContext";

interface TeamMember {
  image: string;
  alt: string;
}

interface ChannelMember {
  image: string;
  alt: string;
  userId: string;
  email: string;
  role?: string;
}

interface TeamMembersGridProps {
  members: TeamMember[];
  showAskCurt?: boolean;
  useProfileAvatars?: boolean;
  channelId?: string;
}

// Function to get member name and role based on alt text (defined outside component to avoid hoisting issues)
const getMemberInfo = (alt: string): {
  name: string;
  role: string;
  email: string;
  bio?: string;
} => {
  if (alt === "Owl #9" || alt === "Innovation Expert" || alt === "Trey Fleming") {
    return {
      name: "Trey Fleming",
      role: "Founder and CEO",
      email: "trey@ispai.io"
    };
  } else if (alt === "Owl #12" || alt === "Supply Chain and Law Expert" || alt === "Morgan Eldred") {
    return {
      name: "Morgan Eldred",
      role: "Advisor - IM, IP, and IT Law",
      email: "morgan.eldred@troutman.com"
    };
  } else if (alt === "GTM Advisor" || alt === "Owl #2" || alt === "Chris Clayton") {
    return {
      name: "Chris Clayton",
      role: "Advisor - Commercial",
      email: "chris.clayton@navigant.com"
    };
  } else if (alt === "Strategic Sourcing" || alt === "Owl #6" || alt === "Karen Watson") {
    return {
      name: "Karen Watson",
      role: "Cofounder and Chief Value Chain Officer",
      email: "karen@keylimeinteractive.com"
    };
  } else if (alt === "Owl #13" || alt === "Pete Dumont") {
    return {
      name: "Pete Dumont",
      role: "Advisor - Construction and Capital Projects / Vice Chair of CURT",
      email: "peter.dumont@conocophillips.com"
    };
  } else if (alt === "AI For All" || alt === "Owl #10" || alt === "Yannis Kalfoglou") {
    return {
      name: "Yannis Kalfoglou",
      role: "Chief Technical Advisor",
      email: "yanniskalfoglou@gmail.com"
    };
  } else if (alt === "Brand Marketing Expert" || alt === "Owl #4" || alt === "Jess Simm") {
    return {
      name: "Jess Simm",
      role: "Chief Marketing Officer",
      email: "jess.simm@ispai.io"
    };
  } else if (alt === "Innovation Logistics" || alt === "Corey Vickers") {
    return {
      name: "Corey Vickers",
      role: "Cofounder and Chief Advisor",
      email: "corey.vickers@conoverbuilding.com"
    };
  } else if (alt === "Blockchain Expert" || alt === "Owl #8" || alt === "Shaan Ray") {
    return {
      name: "Shaan Ray",
      role: "Head of Web3",
      email: "shaan.ray1@gmail.com"
    };
  } else if (alt === "Product Design Expert" || alt === "Owl #11" || alt === "Miguel Escobedo") {
    return {
      name: "Miguel Escobedo",
      role: "Chief Product Officer",
      email: "miguelescobedo@ispai.io"
    };
  } else if (alt === "Finance Expert" || alt === "Owl #5" || alt === "Jim Lawnin") {
    return {
      name: "Jim Lawnin",
      role: "Advisor - Finance and Venture Capital",
      email: "jimlawnin@gmail.com"
    };
  } else if (alt === "Application Development and Integration" || alt === "Head of Application Development and Integration" || alt === "Enrique Barrantes") {
    return {
      name: "Enrique Barrantes",
      role: "Co-founder and CTO",
      email: "enrique.barrantes@ispai.io"
    };
  } else if (alt === "Security Expert" || alt === "Owl #7" || alt === "Mike Mosher") {
    return {
      name: "Mike Mosher",
      role: "Advisor - Security and Risk Management",
      email: "mmosher@securityperspective.com"
    };
  } else if (alt === "GSA Knowledge Network Ambassador" || alt === "Owl #15") {
    return {
      name: "Julia De Rosi",
      role: "GSA Knowledge Network Ambassador",
      email: "julia.derosi@gmail.com"
    };
  } else if (alt === "Global Modularization Expert" || alt === "Owl #14") {
    return {
      name: "Bill Lewis",
      role: "Director of Global Modularization at Microsoft (ret.)",
      email: "lewisww77@hotmail.com"
    };
  } else if (alt === "Chair, CURT Board of Directors") {
    return {
      name: "Jim Ellis",
      role: "Chair, CURT Board of Directors",
      email: "jimellis888@yahoo.com"
    };
  } else if (alt === "CEO of CURT") {
    return {
      name: "Greg Sizemore",
      role: "CEO of CURT",
      email: "greg.sizemore@curt.org"
    };
  } else if (alt === "Sr. VP & General Counsel" || alt === "Nathaniel Sizemore") {
    return {
      name: "Nathaniel Sizemore",
      role: "Sr. VP & General Counsel",
      email: "nathaniel@sizemoreco.com"
    };
  } else if (alt === "Director of Marketing") {
    return {
      name: "Austin Fowler",
      role: "Director of Marketing",
      email: "austin.fowler@curt.org"
    };
  } else if (alt === "Julia De Rosi") {
    return {
      name: "Julia De Rosi",
      role: "Fmr Deputy Assistant Commissioner, Design and Construction",
      email: "julia.derosi@gmail.com",
      bio: "Julia De Rosi served as Deputy Assistant Commissioner for Design and Construction at PBS, overseeing major federal building projects and design standards."
    };
  } else if (alt === "Heather Edwards") {
    return {
      name: "Heather Edwards",
      role: "Chief of Staff & Acting Director, PBS A&E",
      email: "heather.edwards@gsa.gov",
      bio: "Heather Edwards serves as Chief of Staff and Acting Director for PBS Architecture and Engineering, leading federal design and construction initiatives."
    };
  } else if (alt === "Charles Hardy") {
    return {
      name: "Charles Hardy",
      role: "Chief Architect",
      email: "charles.hardy@gsa.gov",
      bio: "Charles Hardy is the Chief Architect at GSA, responsible for establishing design excellence standards across federal buildings."
    };
  } else if (alt === "Louie Ehrlich") {
    return {
      name: "Louie Ehrlich",
      role: "Retired CIO of Chevron, Investor & Strategic Advisor",
      email: "lvehrlich@gmail.com",
      bio: "Louie V. Ehrlich brings more than four decades of leadership in technology, business strategy, and governance. His career includes a 34-year tenure at Chevron, where he held several senior leadership positions, including Chief Information Officer (CIO) and President of Chevron IT Company. Today, Louie serves as CEO of Ehrlich Capital LLC and strategic consultant advising businesses from startups to global enterprises. He has received the Top 10 Global CIO Award, the Transformation CIO Award, and was twice a finalist for the Fisher-Hopper Prize for Lifetime Achievement in CIO Leadership."
    };
  } else if (alt === "Ed Barrows") {
    return {
      name: "Ed Barrows",
      role: "Retired CIO of Occidental Petroleum, Investor & Strategic Advisor",
      email: "eab986@gmail.com",
      bio: "Ed was an Executive Partner with Gartner Executive Programs. Prior to Gartner, Mr. Barrows operated as a CIO for over 25 years and was the Application Executive covering all business lines worldwide for Occidental Petroleum Corporation (Oxy). His industry experience covers the entire energy value chain: upstream, midstream, downstream, and mining. He has extensive experience in M&A/divestitures, having been involved in more than 50 deals in recent years."
    };
  } else {
    return {
      name: "Team Member",
      role: "iSPAI Team",
      email: "info@ispai.io"
    };
  }
};

const TeamMembersGrid: FC<TeamMembersGridProps> = ({
  members,
  showAskCurt = false,
  useProfileAvatars = false,
  channelId
}) => {
  const { openProfileDrawer } = useProfileDrawer();
  const location = useLocation();

  // Detect channel from route (fallback if channelId not provided)
  const channelSlug = location.pathname.includes('curt') ? 'curt' : 
                      location.pathname.includes('usa') ? 'us-federal-government' : 
                      location.pathname.includes('oil-gas') ? 'oil-and-gas' : 'ispai';
  
  // Fetch channel info
  const { data: channelData } = useQuery({
    queryKey: ['channel', channelSlug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('channels')
        .select('*')
        .eq('slug', channelSlug)
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  // Fetch channel members from user_organizations
  const { data: channelMembers } = useQuery({
    queryKey: ['channel-members', channelId],
    queryFn: async () => {
      if (!channelId) return [];
      
      const { data, error } = await supabase
        .from('user_organizations')
        .select(`
          user_id,
          profiles!inner(
            id,
            display_name,
            avatar_url,
            email,
            title
          )
        `)
        .eq('channel_id', channelId);
      
      if (error) throw error;
      return data?.map(uo => ({
        image: (uo.profiles as any).avatar_url || '/placeholder.svg',
        alt: (uo.profiles as any).display_name,
        userId: (uo.profiles as any).id,
        email: (uo.profiles as any).email,
        role: (uo.profiles as any).title
      })) as ChannelMember[] || [];
    },
    enabled: !!channelId
  });

  // Fetch profile avatars and emails from database for hardcoded members
  const { data: profileData } = useQuery({
    queryKey: ['profile-data', members, useProfileAvatars],
    queryFn: async () => {
      if (!useProfileAvatars) return {};
      
      // Extract member names from alt text
      const memberNames = members.map(m => {
        const info = getMemberInfo(m.alt);
        return info.name;
      });
      
      const { data } = await supabase
        .from('profiles')
        .select('display_name, avatar_url, email')
        .in('display_name', memberNames);
      
      // Create a map: displayName → { avatar_url, email }
      return data?.reduce((acc, p) => {
        acc[p.display_name] = {
          avatar_url: p.avatar_url,
          email: p.email
        };
        return acc;
      }, {} as Record<string, { avatar_url?: string; email: string }>) || {};
    },
    enabled: useProfileAvatars
  });

  // Merge hardcoded members with database channel members
  type MergedMember = (TeamMember & { userId?: string; email?: string; role?: string }) | ChannelMember;
  
  const allMembers = useMemo((): MergedMember[] => {
    if (!channelMembers?.length) return members;
    
    // Get display names from hardcoded members
    const hardcodedNames = new Set(members.map(m => getMemberInfo(m.alt).name));
    
    // Filter out DB members that are already in hardcoded list
    const newFromDb = channelMembers.filter(cm => !hardcodedNames.has(cm.alt));
    
    // Return hardcoded members + new DB members
    return [...members, ...newFromDb];
  }, [members, channelMembers]);

  const handleImageClick = async (member: TeamMember | ChannelMember) => {
    // If member has userId from DB, use it directly
    if ('userId' in member && member.userId) {
      openProfileDrawer(member.userId);
      return;
    }
    
    // Fallback to existing lookup logic for hardcoded members
    const info = getMemberInfo(member.alt);
    const displayName = info.name;
    
    // Fetch user ID from profiles table
    const { data } = await supabase
      .from('profiles')
      .select('id')
      .eq('display_name', displayName)
      .single();
    
    if (data?.id) {
      openProfileDrawer(data.id);
    }
  };
  return <div className="mb-6 bg-white p-6 pb-2 rounded-lg border border-slate-200 shadow">
      <h2 className="text-xl font-semibold mb-4">mySPAI Team Profiles</h2>
      <div className="flex overflow-x-auto gap-4 pb-2 scroll-smooth">
        {allMembers.filter(m => m.alt !== "CTO Team Member").map((member, index) => {
        const isChannelMember = 'userId' in member && !!member.userId;
        const memberInfo = isChannelMember 
          ? { name: member.alt, role: (member as ChannelMember).role || 'Team Member', email: (member as ChannelMember).email, bio: undefined }
          : getMemberInfo(member.alt);
        // Determine which image to use: profile avatar (if available) or hardcoded
        const effectiveImage = isChannelMember
          ? member.image
          : (useProfileAvatars && profileData?.[memberInfo.name]?.avatar_url
            ? profileData[memberInfo.name].avatar_url
            : member.image);
        // Use database email if available, otherwise use hardcoded
        const effectiveEmail = isChannelMember
          ? (member as ChannelMember).email
          : (useProfileAvatars && profileData?.[memberInfo.name]?.email
            ? profileData[memberInfo.name].email
            : memberInfo.email);
        return <HoverCard key={index} openDelay={200} closeDelay={300}>
              <HoverCardTrigger asChild>
                <div className="aspect-square relative cursor-pointer transition-transform hover:scale-150 duration-200 flex-shrink-0 w-24" onClick={() => handleImageClick(member as TeamMember | ChannelMember)} data-alt={member.alt}
            style={{
              backgroundImage: `url(${effectiveImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center 35%',
              borderRadius: '0.5rem'
            }} />
              </HoverCardTrigger>
              <HoverCardContent className="w-auto min-w-[200px]">
                <div className="text-center">
                  <p className="font-bold">{memberInfo.name}</p>
                  <p className="text-sm text-muted-foreground">{memberInfo.role}</p>
                  <a href={`mailto:${effectiveEmail}`} className="text-xs text-blue-600 hover:text-blue-800 flex items-center justify-center mt-2" onClick={e => e.stopPropagation()}>
                    <Mail size={12} className="mr-1" />
                    {effectiveEmail}
                  </a>
                </div>
              </HoverCardContent>
            </HoverCard>;
      })}
      </div>
    </div>;
};
export default TeamMembersGrid;